/**
 * Contract Template Management Hook
 *
 * Manages contract templates and instances using localStorage
 * (Will be migrated to Supabase in final phase)
 */

import { useState, useEffect, useCallback } from 'react';
import type {
  ContractTemplate,
  ContractInstance,
  ContractVariableData,
  CONTRACT_TEMPLATE_STORAGE_KEY,
  CONTRACT_INSTANCES_STORAGE_KEY
} from './contract-types';
import { computeDerivedVariables, cloneTemplate } from './contract-utils';

// ============================================================================
// Template Management
// ============================================================================

/**
 * Hook for managing the contract template
 */
export function useContractTemplate() {
  const [template, setTemplate] = useState<ContractTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load template from localStorage
  useEffect(() => {
    try {
      setLoading(true);
      const stored = localStorage.getItem('lendwise_contract_template');

      if (stored) {
        const parsed = JSON.parse(stored) as ContractTemplate;
        setTemplate(parsed);
      }

      setError(null);
    } catch (err) {
      console.error('Error loading contract template:', err);
      setError(err instanceof Error ? err.message : 'Failed to load template');
    } finally {
      setLoading(false);
    }
  }, []);

  // Save template to localStorage
  const saveTemplate = useCallback((newTemplate: ContractTemplate) => {
    try {
      const updated: ContractTemplate = {
        ...newTemplate,
        updatedAt: new Date().toISOString()
      };

      localStorage.setItem('lendwise_contract_template', JSON.stringify(updated));
      setTemplate(updated);
      setError(null);

      return true;
    } catch (err) {
      console.error('Error saving contract template:', err);
      setError(err instanceof Error ? err.message : 'Failed to save template');
      return false;
    }
  }, []);

  // Update a specific section
  const updateSection = useCallback(
    (sectionId: string, updates: Partial<ContractTemplate['sections'][0]>) => {
      if (!template) {
return false;
}

      try {
        const updatedTemplate = cloneTemplate(template);
        const sectionIndex = updatedTemplate.sections.findIndex(s => s.id === sectionId);

        if (sectionIndex === -1) {
          throw new Error(`Section not found: ${sectionId}`);
        }

        updatedTemplate.sections[sectionIndex] = {
          ...updatedTemplate.sections[sectionIndex],
          ...updates
        };

        return saveTemplate(updatedTemplate);
      } catch (err) {
        console.error('Error updating section:', err);
        setError(err instanceof Error ? err.message : 'Failed to update section');
        return false;
      }
    },
    [template, saveTemplate]
  );

  // Reset to default template
  const resetTemplate = useCallback(() => {
    try {
      localStorage.removeItem('lendwise_contract_template');
      setTemplate(null);
      setError(null);
      return true;
    } catch (err) {
      console.error('Error resetting template:', err);
      setError(err instanceof Error ? err.message : 'Failed to reset template');
      return false;
    }
  }, []);

  return {
    template,
    loading,
    error,
    saveTemplate,
    updateSection,
    resetTemplate
  };
}

// ============================================================================
// Instance Management
// ============================================================================

/**
 * Hook for managing contract instances for candidates
 */
export function useContractInstances() {
  const [instances, setInstances] = useState<Record<string, ContractInstance>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load instances from localStorage
  useEffect(() => {
    try {
      setLoading(true);
      const stored = localStorage.getItem('lendwise_contract_instances');

      if (stored) {
        const parsed = JSON.parse(stored) as Record<string, ContractInstance>;
        setInstances(parsed);
      }

      setError(null);
    } catch (err) {
      console.error('Error loading contract instances:', err);
      setError(err instanceof Error ? err.message : 'Failed to load instances');
    } finally {
      setLoading(false);
    }
  }, []);

  // Save instances to localStorage
  const saveInstances = useCallback((newInstances: Record<string, ContractInstance>) => {
    try {
      localStorage.setItem('lendwise_contract_instances', JSON.stringify(newInstances));
      setInstances(newInstances);
      setError(null);
      return true;
    } catch (err) {
      console.error('Error saving contract instances:', err);
      setError(err instanceof Error ? err.message : 'Failed to save instances');
      return false;
    }
  }, []);

  // Get instance for a specific candidate
  const getInstance = useCallback(
    (candidateId: string): ContractInstance | undefined => {
      return instances[candidateId];
    },
    [instances]
  );

  // Create or update instance for a candidate
  const saveInstance = useCallback(
    (candidateId: string, variableData: ContractVariableData, templateId: string, templateVersion: number) => {
      try {
        const existingInstance = instances[candidateId];
        const now = new Date().toISOString();

        const instance: ContractInstance = {
          id: existingInstance?.id || `instance_${Date.now()}`,
          candidateId,
          templateId,
          templateVersion,
          variableData: computeDerivedVariables(variableData),
          checkboxStates: existingInstance?.checkboxStates || {},
          createdAt: existingInstance?.createdAt || now,
          updatedAt: now
        };

        const updatedInstances = {
          ...instances,
          [candidateId]: instance
        };

        return saveInstances(updatedInstances);
      } catch (err) {
        console.error('Error saving contract instance:', err);
        setError(err instanceof Error ? err.message : 'Failed to save instance');
        return false;
      }
    },
    [instances, saveInstances]
  );

  // Update checkbox states for an instance
  const updateCheckboxStates = useCallback(
    (candidateId: string, checkboxStates: Record<string, boolean>) => {
      try {
        const instance = instances[candidateId];
        if (!instance) {
          throw new Error(`Instance not found for candidate: ${candidateId}`);
        }

        const updatedInstance: ContractInstance = {
          ...instance,
          checkboxStates,
          updatedAt: new Date().toISOString()
        };

        const updatedInstances = {
          ...instances,
          [candidateId]: updatedInstance
        };

        return saveInstances(updatedInstances);
      } catch (err) {
        console.error('Error updating checkbox states:', err);
        setError(err instanceof Error ? err.message : 'Failed to update checkboxes');
        return false;
      }
    },
    [instances, saveInstances]
  );

  // Delete instance for a candidate
  const deleteInstance = useCallback(
    (candidateId: string) => {
      try {
        const updatedInstances = { ...instances };
        delete updatedInstances[candidateId];

        return saveInstances(updatedInstances);
      } catch (err) {
        console.error('Error deleting contract instance:', err);
        setError(err instanceof Error ? err.message : 'Failed to delete instance');
        return false;
      }
    },
    [instances, saveInstances]
  );

  return {
    instances,
    loading,
    error,
    getInstance,
    saveInstance,
    updateCheckboxStates,
    deleteInstance
  };
}

// ============================================================================
// Combined Hook
// ============================================================================

/**
 * Hook for managing both template and instances together
 */
export function useContract(candidateId?: string) {
  const templateHook = useContractTemplate();
  const instancesHook = useContractInstances();

  const instance = candidateId ? instancesHook.getInstance(candidateId) : undefined;

  return {
    // Template
    template: templateHook.template,
    templateLoading: templateHook.loading,
    templateError: templateHook.error,
    saveTemplate: templateHook.saveTemplate,
    updateSection: templateHook.updateSection,
    resetTemplate: templateHook.resetTemplate,

    // Instance
    instance,
    instanceLoading: instancesHook.loading,
    instanceError: instancesHook.error,
    saveInstance: instancesHook.saveInstance,
    updateCheckboxStates: instancesHook.updateCheckboxStates,

    // Combined loading state
    loading: templateHook.loading || instancesHook.loading,
    error: templateHook.error || instancesHook.error
  };
}
